import { useEffect, useMemo, useRef, useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useTranslation } from 'react-i18next'
import { ChevronDown, X } from 'lucide-react'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import { useLearnerLibrary } from '@/hooks/learner/useLearnerLibrary'
import { cn } from '@/lib/utils'
import {
  findInitialJourneyTabKey,
  toLearningJourneyData,
  type JourneyCourseSection,
  type JourneyTab,
} from '@/pages/learner/model/learningJourneyModel'
import { parseCoreCourseIdentity } from '@/pages/learner/model/libraryModel'
import { getLearnerErrorMessage } from '@/utils/errorMessage'

interface CourseJourneyDialogProps {
  open: boolean
  onClose: () => void
}

function selectedTabClass(tab: JourneyTab): string {
  if (tab.coreCode === 'A') return 'bg-lc-curriculum-core-a'
  if (tab.coreCode === 'B') return 'bg-lc-curriculum-core-b'
  if (tab.coreCode === 'C') return 'bg-lc-curriculum-core-c'
  if (tab.coreCode === 'D') return 'bg-lc-curriculum-core-d'
  if (tab.coreCode === 'E') return 'bg-lc-curriculum-core-e'
  if (tab.courseTitleType === 'advanced') return 'bg-lc-curriculum-advanced'
  if (tab.courseTitleType === 'elite') return 'bg-lc-curriculum-elite'
  return 'bg-lc-key-500'
}

function findInitialExpandedCourseId(courses: JourneyCourseSection[]): number | null {
  const currentCourse = courses.find((course) =>
    course.chapters.some((chapter) => chapter.status === 'current'),
  )
  return currentCourse?.id ?? courses[0]?.id ?? null
}

function JourneyCourseAccordion({ tab }: { tab: JourneyTab }) {
  const { t } = useTranslation('nav')
  const [expandedCourseId, setExpandedCourseId] = useState<number | null>(
    () => findInitialExpandedCourseId(tab.courses),
  )

  const courseLabel = (course: JourneyCourseSection): string => {
    const identity = parseCoreCourseIdentity(course.title)
    if (!identity) return course.title
    return t('learnerJourney.coreCourseMember', {
      code: identity.code,
      member: identity.memberNumber ?? '',
    })
  }

  return (
    <div className="scrollbar-none flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
      {tab.courses.map((course) => {
        const isExpanded = expandedCourseId === course.id
        const label = courseLabel(course)
        const chapterListId = `journey-course-${course.id}-chapters`
        return (
          <section
            key={course.id}
            className={cn(
              'flex min-h-0 flex-col rounded-2xl bg-lc-disabled-bg px-4 pt-4',
              isExpanded ? 'flex-1 pb-4' : 'shrink-0 pb-6',
            )}
          >
            <div className="shrink-0 px-2">
              <button
                type="button"
                aria-expanded={isExpanded}
                aria-controls={chapterListId}
                onClick={() => setExpandedCourseId((currentId) => (
                  currentId === course.id ? null : course.id
                ))}
                className="flex w-full items-center justify-between gap-4 text-left text-lc-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
              >
                <span className="truncate text-lc-v3-h4 font-bold">{label}</span>
                <span className="flex shrink-0 items-center gap-2">
                  <span className="text-lc-v3-h5 font-semibold">
                    {course.progressPercent}%
                  </span>
                  <ChevronDown
                    className={cn(
                      'size-6 text-lc-v3-gray-600 transition-transform',
                      isExpanded && 'rotate-180',
                    )}
                    aria-hidden="true"
                  />
                </span>
              </button>
              <div
                role="progressbar"
                aria-label={t('learnerJourney.progress', { course: label })}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={course.progressPercent}
                className="mt-4 h-2 overflow-hidden rounded-full bg-lc-v3-gray-300"
              >
                <div
                  className="h-full rounded-full bg-lc-key-500"
                  style={{ width: `${course.progressPercent}%` }}
                />
              </div>
            </div>

            {isExpanded ? (
              <ol
                id={chapterListId}
                className="scrollbar-none mt-6 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto rounded-lg"
              >
                {course.chapters.map((chapter, chapterIndex) => (
                  <li
                    key={chapter.id}
                    className={cn(
                      'flex shrink-0 items-center gap-4 rounded-lg px-4 py-2 text-lc-v3-body-1',
                      chapter.status === 'locked'
                        ? 'bg-lc-disabled-surface text-lc-text-tertiary'
                        : 'bg-lc-bg-primary text-lc-v3-gray-800',
                      chapter.status === 'current' && 'text-lc-text-link',
                    )}
                  >
                    <span className="w-14 shrink-0 text-lc-v3-body-2 font-semibold">
                      {t('learnerJourney.chapter', { chapter: chapterIndex + 1 })}
                    </span>
                    <span className={cn(
                      'min-w-0 flex-1 truncate font-medium',
                      chapter.status === 'current' && 'font-semibold',
                    )}>
                      <ContentMetadataText text={chapter.title} />
                    </span>
                    <span className="flex w-20 shrink-0 items-center justify-end gap-1 whitespace-nowrap">
                      <strong className="font-semibold">{chapter.completedLessons}</strong>
                      <span className={cn(
                        'font-medium',
                        chapter.status !== 'locked' && 'text-lc-text-tertiary',
                      )}>
                        / {chapter.totalLessons}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            ) : null}
          </section>
        )
      })}
    </div>
  )
}

export function CourseJourneyDialog({ open, onClose }: CourseJourneyDialogProps) {
  const { t } = useTranslation(['nav', 'common'])
  const [selectedTabKey, setSelectedTabKey] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const libraryQuery = useLearnerLibrary({ enabled: open })
  const journey = useMemo(
    () => libraryQuery.data ? toLearningJourneyData(libraryQuery.data) : null,
    [libraryQuery.data],
  )
  const fallbackTabKey = journey ? findInitialJourneyTabKey(journey.tabs) : null
  const selectedTab = journey?.tabs.find((tab) => tab.key === selectedTabKey)
    ?? journey?.tabs.find((tab) => tab.key === fallbackTabKey)
    ?? null
  useFocusTrap(dialogRef, open)

  useEffect(() => {
    if (!open) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose, open])

  if (!open) return null

  const tabLabel = (tab: JourneyTab) => {
    if (tab.coreCode) {
      return t('nav:learnerJourney.coreCourse', { code: tab.coreCode })
    }
    if (tab.courseTitleType === 'advanced') {
      return t('nav:learnerJourney.tabs.advanced')
    }
    if (tab.courseTitleType === 'elite') {
      return t('nav:learnerJourney.tabs.elite')
    }
    return tab.title
  }
  const title = journey?.curriculumTitle
    ? t('nav:learnerJourney.titleWithCourse', { course: journey.curriculumTitle })
    : t('nav:learnerJourney.title')

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-lc-bg-overlay p-4"
      role="presentation"
      onMouseDown={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="course-journey-title"
        className="flex h-[634px] max-h-[calc(100dvh-32px)] w-full max-w-[480px] flex-col gap-6 rounded-3xl border border-lc-v3-gray-300 bg-lc-bg-primary px-4 pb-5 pt-4 shadow-lc-v3-card"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="flex shrink-0 items-center justify-between bg-lc-bg-primary pl-2 pr-1">
          <h2
            id="course-journey-title"
            className="text-lc-v3-h5 font-bold text-lc-v3-gray-800"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('nav:learnerJourney.close')}
            className="inline-flex size-6 items-center justify-center rounded-full text-lc-v3-gray-600 hover:bg-lc-v3-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </header>

        {journey && journey.tabs.length > 0 ? (
          <div
            className="scrollbar-none flex h-10 shrink-0 items-center gap-2 overflow-x-auto"
            role="tablist"
            aria-label={t('nav:learnerJourney.tabsLabel')}
          >
            {journey.tabs.map((tab) => {
              const isSelected = selectedTab?.key === tab.key
              const isEmpty = tab.totalLessons === 0
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  disabled={isEmpty}
                  onClick={() => setSelectedTabKey(tab.key)}
                  className={cn(
                    'h-10 min-w-28 flex-1 rounded-xl border px-4 text-lc-v3-label-lg font-semibold',
                    isSelected
                      ? cn('border-transparent text-lc-text-inverse', selectedTabClass(tab))
                      : 'border-lc-v3-gray-300 bg-lc-v3-gray-200 text-lc-text-secondary',
                    isEmpty && 'bg-lc-v3-gray-200 text-lc-text-disabled',
                  )}
                >
                  {tabLabel(tab)}
                </button>
              )
            })}
          </div>
        ) : null}

        {libraryQuery.isLoading ? (
          <section className="flex min-h-0 flex-1 flex-col rounded-2xl bg-lc-disabled-bg p-4">
            <div className="flex flex-1 items-center justify-center" role="status" aria-live="polite">
              <p className="text-lc-v3-body-2 font-semibold text-lc-text-secondary">
                {t('nav:learnerJourney.loading')}
              </p>
            </div>
          </section>
        ) : libraryQuery.isError ? (
          <section className="flex min-h-0 flex-1 flex-col rounded-2xl bg-lc-disabled-bg p-4">
            <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center" role="alert">
              <p className="text-lc-v3-body-2 font-semibold text-lc-text-secondary">
                {getLearnerErrorMessage(
                  libraryQuery.error,
                  t,
                  'nav:learnerJourney.error',
                )}
              </p>
              <button
                type="button"
                onClick={() => void libraryQuery.refetch()}
                className="rounded-xl bg-lc-key-500 px-4 py-2 text-lc-v3-label-lg font-semibold text-lc-text-inverse"
              >
                {t('nav:learnerJourney.retry')}
              </button>
            </div>
          </section>
        ) : !journey || journey.tabs.length === 0 ? (
          <section className="flex min-h-0 flex-1 flex-col rounded-2xl bg-lc-disabled-bg p-4">
            <div className="flex flex-1 items-center justify-center text-center" role="status">
              <p className="text-lc-v3-body-2 font-semibold text-lc-text-secondary">
                {t('nav:learnerJourney.empty')}
              </p>
            </div>
          </section>
        ) : selectedTab && selectedTab.totalLessons > 0 ? (
          <JourneyCourseAccordion key={selectedTab.key} tab={selectedTab} />
        ) : (
          <section className="flex min-h-0 flex-1 flex-col rounded-2xl bg-lc-disabled-bg p-4">
            <div className="flex flex-1 items-center justify-center text-center" role="status">
              <p className="text-lc-v3-body-2 font-semibold text-lc-text-secondary">
                {t('nav:learnerJourney.emptyCourse')}
              </p>
            </div>
          </section>
        )}
      </div>
    </div>
  )
}
